import React, {ReactNode} from 'react';
import {
  StyleProp,
  StyleSheet,
  Text as RNText,
  TextProps as RNTextProps,
  TextStyle,
} from 'react-native';

import theme from '../../style/theme';
import type {TypographyWeights} from '../../types/theme';

export type TextVariant =
  | 'body'
  | 'body2'
  | 'caption'
  | 'heading0'
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'heading4';

export interface TextProps extends RNTextProps {
  style?: StyleProp<TextStyle>;
  children: ReactNode;
  variant?: TextVariant;
  align?: 'center' | 'left' | 'right' | 'justify';
}

// https://reactnative.dev/docs/text#limited-style-inheritance:
// "The recommended way to use consistent fonts and sizes across your
// application is to create a component MyAppText that includes them
// and use this component across your app."
const Text = ({style, children, variant, align, ...props}: TextProps) => {
  const internalStyles: TextStyle[] = [allStyles[variant || 'body']];
  if (align) {
    internalStyles.push({textAlign: align});
  }

  return (
    <RNText style={[internalStyles, style]} {...props}>
      {children}
    </RNText>
  );
};

export const fontFamily = theme.typography?.fontFamily ?? 'Rubik';
export const fontFamilyBold = theme.typography?.fontFamilyBold ?? 'Rubik-Bold';
export const fontFamilyStyle = {
  fontFamily: theme.typography?.fontFamily ?? 'Rubik',
};

export function getFontByWeight(weight: keyof TypographyWeights): string {
  return theme.typography?.weights?.[weight]?.fileName ?? 'Rubik';
}

const selectable = {
  // The `userSelect` style prop gets mapped to the `selectable` component prop.
  // All 'auto'/'text'/'contain'/'all' map to `selectable: true` on Native. Source:
  // https://github.com/facebook/react-native/blob/main/packages/react-native/Libraries/Text/Text.js#L292
  // We are using this instead of the `selectable` prop to keep it uniform because
  // react-native-markdown-display/@ronradtke/react-native-markdown-display doesn't support it
  // and we share these styles with Markdown
  // - Does this plugin allows copying content? (selectable) #126 - https://github.com/iamacup/react-native-markdown-display/issues/126
  // - Select and copy text by using rules #173 - https://github.com/iamacup/react-native-markdown-display/issues/173
  userSelect: 'text',
};

const commonTextStyles = {
  ...fontFamilyStyle,
  ...selectable,
};

// Also used in Markdown
export const textBodyStyles = {
  ...commonTextStyles,
  fontSize: theme.size.text.body1,
};

export const textBodyStyles2 = {
  ...commonTextStyles,
  fontSize: theme.size.text.body2,
};

// Also used in Markdown
export const textCaptionStyles = {
  ...commonTextStyles,
  fontSize: theme.size.text.caption,
  lineHeight: 16,
};

const heading = {
  ...commonTextStyles,
  color: theme.colour.text.heading.primary,
  fontWeight: 'bold' as const,
};

const allStyles = StyleSheet.create({
  body: textBodyStyles,
  body2: textBodyStyles2,
  caption: textCaptionStyles,
  heading0: {
    ...heading,
    fontSize: theme.size.text.heading0,
  },
  heading1: {
    ...heading,
    fontSize: theme.size.text.heading1,
  },
  heading2: {
    ...heading,
    fontSize: theme.size.text.heading2,
  },
  heading3: {
    ...heading,
    fontSize: theme.size.text.heading3,
  },
  heading4: {
    ...heading,
    fontSize: theme.size.text.heading4,
  },
});

export default Text;
